'use client';

import { ISalePageDetailV2Response } from '@/api/types';
import { handleNewWindowLinkNavigation } from '@/lib/newWindow';
import { URLS } from '@/urls';
import { Element } from '@vuddy/components';
import { ReactNode } from 'react';
import { SalePagePurchaseGuideSections } from './PurchaseGuideSection';
import * as styles from './classPurchaseGuide.css';

const REFUND_POLICY_LINK = `https://help.vuddy.io/1adc783a-5265-805a-a953-f6f1b7a787f2#:~:text=%EC%9E%88%EB%8F%84%EB%A1%9D%20%EB%AA%85%ED%99%95%ED%9E%88%20%ED%91%9C%EC%8B%9C%EB%90%A9%EB%8B%88%EB%8B%A4.-,%EC%A0%9C23%EC%A1%B0%20%ED%81%B4%EB%9E%98%EC%8A%A4%20%EC%B2%AD%EC%95%BD%EC%B2%A0%ED%9A%8C%20%EB%93%B1,-1`;
const REFUND_POLICY_KEYWORD = '환불 정책';

const renderTextWithRefundPolicyLink = (text: string): ReactNode => {
  if (!text.includes(REFUND_POLICY_KEYWORD)) {
    return text;
  }

  const parts = text.split(REFUND_POLICY_KEYWORD);
  return (
    <>
      {parts[0]}
      <Element
        as="a"
        href={REFUND_POLICY_LINK}
        target="_blank"
        rel="noopener noreferrer"
        onClick={event => {
          handleNewWindowLinkNavigation(event, REFUND_POLICY_LINK);
        }}
        typography="bodyXsAccent"
        color="tertiary"
        className={styles.refundPolicyLink}>
        {REFUND_POLICY_KEYWORD}
      </Element>
      {parts[1]}
    </>
  );
};

const CLASS_PURCHASE_GUIDE_SECTIONS: {
  title: string;
  items: string[];
}[] = [
  {
    title: '수강 안내',
    items: [
      '해당 상품은 온라인 강의 콘텐츠로, 구매 완료 후 [강의실]에서 확인하실 수 있습니다.',
      '콘텐츠의 제공 형식 및 상세 내용은 판매 페이지에 명시된 정보를 기준으로 합니다.',
    ],
  },
  {
    title: '환불 안내',
    items: [
      '환불 신청은 고객센터(help.vuddy.io)로 문의해주세요.',
      '환불 관련한 구체적인 규정은 이용약관의 환불 정책에 따라 진행됩니다.',
      '환불은 구매일로부터 설정된 정상(유료)수강기간 내에서만 환불이 가능합니다.',
      '평생수강권의 경우, 정상(유료)수강기간은 180일이며, 이후 기간은 무료 연장 수강기간에 해당합니다. 무료 연장 수강기간에는 환불이 불가합니다.',
      '환불 가능 기간이 경과하거나, 시청률이 50%를 초과한 경우에는 환불이 불가합니다.',
    ],
  },
  {
    title: '유의사항',
    items: [
      '구매한 이용권은 계정에 귀속되며 타인 양도/선물이 불가합니다.',
      '강의자료 다운로드, 보너스 영상은 시청률 계산에서 제외됩니다.',
      '콘텐츠 및 강의자료 무단 복제·배포 등은 법적 책임이 발생할 수 있습니다.',
      '이용 권한이 부여된 후에는 계정/기기 문제 등 개인 사유로 인한 환불은 불가합니다.',
      '상황에 따라 할인이나 프로모션은 별도 안내 없이 조기 종료되거나 변경될 수 있습니다.',
      '수강 시작일이 지정된 상품은 임의로 변경할 수 없습니다.',
      '수강 유예, 휴강, 수강 기간 연장은 제공되지 않습니다.',
      '부정행위가 확인될 경우 수강 제한 또는 강의 이용 중단 조치가 이루어질 수 있으며, 이 경우 환불은 불가합니다.',
    ],
  },
];

const PHYSICAL_CLASS_PURCHASE_GUIDE_SECTIONS: {
  title: string;
  items: string[];
}[] = [
  {
    title: '수강 안내',
    items: [
      '해당 상품은 온라인 강의 콘텐츠로, 구매 완료 후 [강의실]에서 확인하실 수 있습니다.',
      '콘텐츠와 실물 상품의 제공 형식 및 상세 내용은 판매 페이지에 명시된 정보를 기준으로 합니다.',
      '실물 상품이 포함된 경우 판매 페이지에 명시된 정보를 기준으로 배송이 진행됩니다.',
      '배송 방법: 택배',
      '배송 지역: 전국',
      '배송 비용: 무료',
    ],
  },
  {
    title: '환불 안내',
    items: [
      '환불 신청은 고객센터(help.vuddy.io)로 문의해주세요.',
      '환불 관련한 구체적인 규정은 이용약관의 환불 정책에 따라 진행됩니다.',
      '영상 이용권의 환불은 구매일로부터 설정된 정상(유료)수강기간 내에서만 환불이 가능합니다. ',
      '평생수강권의 경우, 정상(유료)수강기간은 180일이며, 이후 기간은 무료 연장 수강기간에 해당합니다. 무료 연장 수강기간에는 환불이 불가합니다.',
      '실물 상품의 경우 구매일 기준 7일 이내, 미개봉.미훼손 시 환불.교환 가능합니다.',
      '영상 이용권의 경우 환불 가능 기간이 경과하거나, 시청률이 50%를 초과한 경우에는 환불이 불가합니다.',
      '실물 상품의 경우 단순 변심시 왕복 배송비는 고객 부담이며, 하자/오배송 시 배송비는 회사에서 부담합니다.',
    ],
  },
  {
    title: '유의사항',
    items: [
      '구매한 이용권은 계정에 귀속되며 타인 양도/선물이 불가합니다.',
      '강의자료 다운로드, 보너스 영상은 시청률 계산에서 제외됩니다.',
      '실물 상품은 반품 확인(회수 완료) 후 환불이 진행됩니다.',
      '실물 상품 개봉·훼손·구성품 누락 시 환불이 불가합니다.',
      '콘텐츠 및 강의자료 무단 복제·배포 등은 법적 책임이 발생할 수 있습니다.',
      '이용 권한이 부여된 후에는 계정/기기 문제 등 개인 사유로 인한 환불은 불가합니다.',
      '상황에 따라 할인이나 프로모션은 별도 안내 없이 조기 종료되거나 변경될 수 있습니다.',
      '수강 시작일이 지정된 상품은 임의로 변경할 수 없습니다.',
      '수강 유예, 휴강, 수강 기간 연장은 제공되지 않습니다.',
      '부정행위가 확인될 경우 수강 제한 또는 강의 이용 중단 조치가 이루어질 수 있으며, 이 경우 환불은 불가합니다.',
      '불량으로 인한 교환은 동일한 상품으로만 가능합니다.',
    ],
  },
];

const CLASS_PURCHASE_GUIDE_FAQ = {
  label: '자주 묻는 질문',
  href: URLS.FAQ,
};

interface ClassPurchaseGuideProps {
  salePage?: Partial<ISalePageDetailV2Response>;
}

export const ClassPurchaseGuide = ({ salePage }: ClassPurchaseGuideProps) => {
  const hasPhysicalProduct =
    salePage?.clazz?.classProducts?.some(
      product => product.hasPhysicalProduct
    ) ?? false;

  const rawSections = hasPhysicalProduct
    ? PHYSICAL_CLASS_PURCHASE_GUIDE_SECTIONS
    : CLASS_PURCHASE_GUIDE_SECTIONS;

  // SalePagePurchaseGuideSections 형식에 맞게 변환
  const sections = rawSections.map(section => ({
    title: section.title,
    description: section.items.map(item =>
      renderTextWithRefundPolicyLink(item)
    ),
  }));

  return (
    <SalePagePurchaseGuideSections
      title="수강 / 환불 안내"
      sections={sections}
      qna={CLASS_PURCHASE_GUIDE_FAQ}
    />
  );
};
